/**
 * PaymentSuccess - Page shown after successful Stripe payment
 * Confirms subscription and redirects to app
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';

interface PaymentSuccessProps {
  onContinue: () => void;
}

const PaymentSuccess: React.FC<PaymentSuccessProps> = ({ onContinue }) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Auto-redirect after 5 seconds
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onContinue();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onContinue]);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3
    }}>
      <Container maxWidth="sm">
        <Paper 
          elevation={8} 
          sx={{ 
            p: 6, 
            borderRadius: 4, 
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Confetti decoration */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: 'linear-gradient(90deg, #003399, #FFCC00, #4CAF50, #FF4444, #003399)'
          }} />

          {/* Success Icon */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: '#4CAF50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)'
            }}>
              <CheckIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#003399', mb: 2 }}>
            Pagamento Completato! 🎉
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Grazie per il tuo acquisto! Il tuo abbonamento è ora attivo e puoi iniziare 
            a generare i tuoi progetti Erasmus+ con tutte le funzionalità premium.
          </Typography>

          {/* What's Next */}
          <Box sx={{ 
            bgcolor: 'rgba(0, 51, 153, 0.05)', 
            borderRadius: 2, 
            p: 3, 
            mb: 4,
            textAlign: 'left'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
              <CelebrationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Cosa puoi fare ora:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Genera progetti Erasmus+ completi con AI
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Esporta in formato Word professionale
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Accedi alla tua area personale "My Projects"
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Configura i dati di fatturazione per le prossime fatture
                </Typography>
              </li>
            </Box>
          </Box>

          {/* Continue Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onContinue}
            sx={{
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.1rem',
              bgcolor: '#003399',
              '&:hover': { bgcolor: '#002266' }
            }}
          >
            Inizia a Creare Progetti
          </Button>

          {/* Auto-redirect notice */}
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CircularProgress size={16} sx={{ color: '#003399' }} />
            <Typography variant="body2" color="text.secondary">
              Redirect automatico in {countdown} secondi...
            </Typography>
          </Box>

          {/* Receipt notice */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            📧 Riceverai una email di conferma con i dettagli del pagamento
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PaymentSuccess;
